import { useEffect, useMemo, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useFixedRidesActions, useRideStationActions } from "@ryde/redux";
import {
  CourseRouteTypeEnum,
  DEFAULT_PASSENGER_SEATS_COUNT,
  DirectionEnum,
  DropdownAvailableIconsEnum,
  dropdownPassengersWithStationsAtom,
  dropdownStationListAtom,
  filterHomeStationsCategory,
  filterWorkStationsCategory,
  FixCourseMergingMethodEnum,
  getPassengerDestinationStationIds,
  getPassengerSourceStationsIds,
  getRealRouteStationIds,
  getRideRouteType,
  getRideStationOptionsFromSystemParams,
  IDropdownItem,
  IDropdownItemStations,
  IRideDto,
  isStationRouteWaypointStation,
  isValidPositiveNumber,
  Log,
  mergeNumericArrays,
  normalizePassengersDropdownItems,
  RideAddPassengerViewStationsEnum,
  rideStationsCollapsedIdsAF,
  shouldConfirmConvertToDynamicOnPassengerAdd,
  sortDropdownItemsByArray,
  SYSTEM_PARAMS_STORE_ROOT_SITE_ID,
  systemSettingsDtoAtom,
  TRestrictedDirection,
  useDrawers,
  useRideAllStationsCollapsed,
  useSystemParams,
  useSystemParamsForSite,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import {
  AddPassengerFormNames,
  IAddPassengerFormModel,
} from "./ride-add-passenger.models";

export interface IUseAddPassenger {
  rideDto: IRideDto;
  direction: DirectionEnum;
}

export const useAddPassenger = ({ rideDto, direction }: IUseAddPassenger) => {
  const { t } = useTranslation();
  const [isPassengersDataLoading, startTransition] = useTransition();
  const isPickUp = direction === DirectionEnum.PickUp;

  const isShuttle = useMemo(() => {
    return getRideRouteType(rideDto) === CourseRouteTypeEnum.Shuttle;
  }, [rideDto]);

  const isFlex = useMemo(() => {
    return getRideRouteType(rideDto) === CourseRouteTypeEnum.Flex;
  }, [rideDto]);

  const isNeverMergeFlex = useMemo(() => {
    if (!isFlex) {
      return false;
    }

    return (
      rideDto.metadata?.mergingMethod ===
        FixCourseMergingMethodEnum.NeverMerge ||
      rideDto.courseMergingMethod === FixCourseMergingMethodEnum.NeverMerge
    );
  }, [isFlex, rideDto.courseMergingMethod, rideDto.metadata?.mergingMethod]);

  const stationsDisplayOptions = useMemo(() => {
    if (isShuttle) {
      return [RideAddPassengerViewStationsEnum.DisplayRideStations];
    }

    if (isFlex) {
      const flexOptions = [
        RideAddPassengerViewStationsEnum.DisplayRideStations,
        RideAddPassengerViewStationsEnum.DisplayRideAndCourseStations,
      ];

      if (!isNeverMergeFlex) {
        // Allow-merge flex only: shuttle-like NeverMerge keeps two station-filter modes.
        flexOptions.push(RideAddPassengerViewStationsEnum.DisplayAllStations);
      }

      return flexOptions;
    }

    return [
      RideAddPassengerViewStationsEnum.DisplayRideStations,
      RideAddPassengerViewStationsEnum.DisplayAllStations,
    ];
  }, [isFlex, isNeverMergeFlex, isShuttle]);

  const defaultViewStationsDisplayOption = useMemo(() => {
    if (isShuttle) {
      return RideAddPassengerViewStationsEnum.DisplayRideStations;
    }

    if (isFlex) {
      return RideAddPassengerViewStationsEnum.DisplayRideAndCourseStations;
    }

    return RideAddPassengerViewStationsEnum.DisplayAllStations;
  }, [isFlex, isShuttle]);

  const methods = useForm<IAddPassengerFormModel>({
    defaultValues: {
      passengerAddId: undefined,
      stationFromId: undefined,
      stationToId: undefined,
      seats: DEFAULT_PASSENGER_SEATS_COUNT,
      viewStationsOption: defaultViewStationsDisplayOption,
    },
  });

  const passengersDropdownList = useRecoilValue(
    dropdownPassengersWithStationsAtom,
  );

  const setStationsCollapsedIds = useSetRecoilState(
    rideStationsCollapsedIdsAF(rideDto.id || 0),
  );

  const stationsDropdownList = useRecoilValue(dropdownStationListAtom);

  const { getValues, setValue, watch } = methods;
  const { rideAddPassengerAction, fetchDropdownPassengersAndStations } =
    useRideStationActions();
  const { openEditFormDrawer, openNewOrDuplicateDrawer } = useDrawers();
  const { dynamicConvertEditRideConfirmed } = useFixedRidesActions();
  const allStationsCollapsed = useRideAllStationsCollapsed(rideDto);

  const passengerWatchedId = watch("passengerAddId");
  const stationFromWatchedId = watch("stationFromId");
  const stationToWatchedId = watch("stationToId");
  const viewStationSwitchOption = watch("viewStationsOption");

  const selectedPassenger = useMemo(() => {
    if (!passengerWatchedId) {
      return undefined;
    }

    return passengersDropdownList?.find(x => x.id === passengerWatchedId);
  }, [passengerWatchedId, passengersDropdownList]);

  const systemParamsSiteId = useMemo(() => {
    // Field defaults follow the picked passenger's site; before pick they follow the ride site.
    if (isValidPositiveNumber(selectedPassenger?.siteId)) {
      return selectedPassenger.siteId;
    }

    return rideDto.siteId;
  }, [rideDto.siteId, selectedPassenger?.siteId]);

  const siteParams = useSystemParamsForSite(systemParamsSiteId);
  const generalParams = useSystemParams();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const companyWideParams =
    systemSettingsDto?.paramsBySite?.[SYSTEM_PARAMS_STORE_ROOT_SITE_ID] ??
    generalParams;
  const rideStationOptions = useMemo(
    () => getRideStationOptionsFromSystemParams(siteParams),
    [siteParams],
  );
  const stationsForHomeCategories = siteParams.stationsForHome;
  const stationsForWorkCategories = siteParams.stationsForWork;

  const onlyExistingStations =
    viewStationSwitchOption ===
    RideAddPassengerViewStationsEnum.DisplayRideStations;
  const onlyCourseTemplateStations =
    viewStationSwitchOption ===
    RideAddPassengerViewStationsEnum.DisplayRideAndCourseStations;

  const existingPassengerIds = useMemo(() => {
    return rideDto?.metadata?.passengersInfo?.map(x => +x.id) || [];
  }, [rideDto?.metadata?.passengersInfo]);

  const existingOrPotentialStationIds = useMemo(() => {
    const rideStationsIds = getRealRouteStationIds(
      rideDto?.metadata?.stationsInfo,
    );
    const waypointStationIds = new Set(
      (rideDto?.metadata?.stationsInfo ?? [])
        .filter(isStationRouteWaypointStation)
        .map(station => station.id),
    );

    if (!onlyCourseTemplateStations) {
      return rideStationsIds;
    }

    const courseStationIds = (rideDto?.courseStationsIds || []).filter(
      id => !waypointStationIds.has(id),
    );
    const combinedStationIds = mergeNumericArrays(
      courseStationIds,
      rideStationsIds,
    );
    // Course-path mode: map waypoints are not selectable passenger stops.
    return combinedStationIds.filter(id => !waypointStationIds.has(id));
  }, [
    onlyCourseTemplateStations,
    rideDto?.courseStationsIds,
    rideDto?.metadata?.stationsInfo,
  ]);

  const existingOrPotentialStationIdsSet = useMemo(
    () => new Set(existingOrPotentialStationIds),
    [existingOrPotentialStationIds],
  );

  const passengerDropdownListFiltered: IDropdownItem[] = useMemo(() => {
    // Pass paramsBySite so each row is labeled with that passenger's site, not the selected one.
    const passenger = normalizePassengersDropdownItems({
      passengersList: passengersDropdownList,
      direction: direction as TRestrictedDirection,
      excludeIds: existingPassengerIds,
      stationOptions: rideStationOptions,
      companyParams: companyWideParams,
      paramsBySite: systemSettingsDto?.paramsBySite,
    });
    return passenger;
  }, [
    companyWideParams,
    direction,
    existingPassengerIds,
    passengersDropdownList,
    rideStationOptions,
    systemSettingsDto?.paramsBySite,
  ]);

  const stationsDropdownListFiltered: IDropdownItemStations[] = useMemo(() => {
    const activeStations = stationsDropdownList?.filter(s => s?.isActive);

    if (!onlyExistingStations && !onlyCourseTemplateStations) {
      return activeStations || [];
    }

    return (
      activeStations?.filter(x =>
        existingOrPotentialStationIdsSet.has(+x.value),
      ) || []
    );
  }, [
    existingOrPotentialStationIdsSet,
    onlyCourseTemplateStations,
    onlyExistingStations,
    stationsDropdownList,
  ]);

  const boldStationsInIds = useMemo(() => {
    const passenger = passengersDropdownList?.find(
      x => x.id === passengerWatchedId,
    );

    if (
      !passenger ||
      (!passenger.sourceStation1Id && !passenger.sourceStation2Id)
    ) {
      return [];
    }

    const boldStationInIds: number[] = [];

    if (passenger.sourceStation2Id) {
      boldStationInIds.push(passenger.sourceStation2Id);
    }

    if (passenger.sourceStation1Id) {
      boldStationInIds.push(passenger.sourceStation1Id);
    }

    return boldStationInIds;
  }, [passengerWatchedId, passengersDropdownList]);

  const stationsInDropdownList = useMemo(() => {
    if (
      !passengerWatchedId ||
      !stationsDropdownListFiltered?.length ||
      !passengersDropdownList?.length
    ) {
      return stationsDropdownListFiltered;
    }

    return sortDropdownItemsByArray(
      stationsDropdownListFiltered,
      boldStationsInIds,
    );
  }, [
    boldStationsInIds,
    passengerWatchedId,
    passengersDropdownList?.length,
    stationsDropdownListFiltered,
  ]);

  const optionIconMapping = useMemo(() => {
    const optionIconMap = new Map();

    stationsDropdownList?.forEach(station => {
      if (station.id && !optionIconMap.has(station.id) && !station.isOfficial) {
        optionIconMap.set(station.id, DropdownAvailableIconsEnum.homeIcon);
      }
    });

    return optionIconMap;
  }, [stationsDropdownList]);

  const boldStationsOutIds = useMemo(() => {
    const passenger = passengersDropdownList?.find(
      x => x.id === passengerWatchedId,
    );

    if (
      !passenger ||
      (!passenger.destinationStation1Id && !passenger.destinationStation2Id)
    ) {
      return [];
    }

    const boldStationOutIds: number[] = [];

    if (passenger.destinationStation2Id) {
      boldStationOutIds.push(passenger.destinationStation2Id);
    }

    if (passenger.destinationStation1Id) {
      boldStationOutIds.push(passenger.destinationStation1Id);
    }

    return boldStationOutIds;
  }, [passengerWatchedId, passengersDropdownList]);

  const stationsOutDropdownList = useMemo(() => {
    if (
      !passengerWatchedId ||
      !stationsDropdownListFiltered?.length ||
      !passengersDropdownList?.length
    ) {
      return stationsDropdownListFiltered;
    }

    return sortDropdownItemsByArray(
      stationsDropdownListFiltered,
      boldStationsOutIds,
    );
  }, [
    boldStationsOutIds,
    passengerWatchedId,
    passengersDropdownList?.length,
    stationsDropdownListFiltered,
  ]);
  const boldStationsFromIds = useMemo(
    () => (isPickUp ? boldStationsInIds : boldStationsOutIds),
    [boldStationsInIds, boldStationsOutIds, isPickUp],
  );

  const boldStationsToIds = useMemo(
    () => (isPickUp ? boldStationsOutIds : boldStationsInIds),
    [boldStationsInIds, boldStationsOutIds, isPickUp],
  );

  const stationsFromDropdownList = useMemo(() => {
    if (isPickUp) {
      return filterHomeStationsCategory({
        passengers: selectedPassenger && [selectedPassenger],
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: stationsInDropdownList,
      });
    } else {
      return filterWorkStationsCategory({
        passengers: selectedPassenger && [selectedPassenger],
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: stationsOutDropdownList,
      });
    }
  }, [
    isPickUp,
    selectedPassenger,
    stationsForHomeCategories,
    stationsForWorkCategories,
    stationsInDropdownList,
    stationsOutDropdownList,
  ]);

  const stationsToDropdownList = useMemo(() => {
    if (isPickUp) {
      return filterWorkStationsCategory({
        passengers: selectedPassenger && [selectedPassenger],
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: stationsOutDropdownList,
      });
    } else {
      return filterHomeStationsCategory({
        passengers: selectedPassenger && [selectedPassenger],
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: stationsInDropdownList,
      });
    }
  }, [
    isPickUp,
    selectedPassenger,
    stationsForHomeCategories,
    stationsForWorkCategories,
    stationsInDropdownList,
    stationsOutDropdownList,
  ]);

  const passengerToStationAddHandler = useEvent(async () => {
    const passengerAddId = getValues("passengerAddId");
    const stationFromId = getValues("stationFromId");
    const stationToId = getValues("stationToId");
    const seats = getValues("seats") || DEFAULT_PASSENGER_SEATS_COUNT;

    let rideDtoForAddPassenger: Partial<IRideDto> = rideDto;

    // New stops or wrong route order: ask convert on the client. Shift-gap vs station clocks stays on backend.
    if (
      shouldConfirmConvertToDynamicOnPassengerAdd({
        rideDto,
        stationFromId,
        stationToId,
      })
    ) {
      const convertResult = await dynamicConvertEditRideConfirmed();

      if (!convertResult.success) {
        warnAndToastSimpleResult(convertResult, t);
        return;
      }

      // Dry-save must use the converted DTO (courseId cleared), same as edit form + recoil after OK.
      rideDtoForAddPassenger = convertResult.data ?? rideDto;
    }

    const rideAddPassengerActionResult = await rideAddPassengerAction({
      rideDto: rideDtoForAddPassenger,
      passengerAddId,
      stationFromId,
      stationToId,
      seats,
    });

    if (rideAddPassengerActionResult.success) {
      Log.debug("Passenger added successfully");

      if (allStationsCollapsed) {
        /*  changed for  https://goryde.atlassian.net/browse/RYDE-4726, left if needed opposite logic
        setStationsCollapsedIds(prev => {
          const updatedArray = [...prev, stationFromId, stationToId]
            .filter(x => x)
            .map(x => Number(x));
          return unique(updatedArray);
        }); */
        setStationsCollapsedIds([]);
      }

      setValue("passengerAddId", undefined);
      setValue("stationFromId", undefined);
      setValue("stationToId", undefined);
      setValue("seats", DEFAULT_PASSENGER_SEATS_COUNT);
    } else {
      warnAndToastSimpleResult(rideAddPassengerActionResult, t);
    }
  });

  const openPassengerCard = useEvent(() => {
    if (!passengerWatchedId) {
      return;
    }

    openEditFormDrawer({
      entity: "passenger",
      id: passengerWatchedId,
    });
  });
  const createNewPassenger = useEvent(async () => {
    const addPassengerResult = await openNewOrDuplicateDrawer({
      entity: "passenger",
    });
    const newId = addPassengerResult.data?.id;

    if (!newId) {
      Log.error("Cant create passenger");
    }

    setValue(AddPassengerFormNames.passengerAddId, newId);
  });

  const openStationFromCard = useEvent(() => {
    if (!stationFromWatchedId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: stationFromWatchedId,
    });
  });

  const openStationToCard = useEvent(() => {
    if (!stationToWatchedId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: stationToWatchedId,
    });
  });
  const createNewStation = useEvent(
    async (fieldName: keyof typeof AddPassengerFormNames) => {
      const addStationResult = await openNewOrDuplicateDrawer({
        entity: "station",
      });
      const newId = addStationResult.data?.id;

      if (!newId) {
        Log.error("Cannot create station");
        return;
      }

      setValue(fieldName, newId);
    },
  );

  const createNewStationTo = useEvent(() =>
    createNewStation(AddPassengerFormNames.stationToId),
  );

  const createNewStationFrom = useEvent(() =>
    createNewStation(AddPassengerFormNames.stationFromId),
  );

  useEffect(() => {
    // Re-open add panel or switch ride: restore flex default (ride + course path).
    setValue("viewStationsOption", defaultViewStationsDisplayOption);
  }, [defaultViewStationsDisplayOption, rideDto.id, setValue]);

  useEffect(() => {
    const currentOption = getValues("viewStationsOption");

    // e.g. DisplayAllStations no longer allowed after NeverMerge flex — fall back to default.
    if (!stationsDisplayOptions.includes(currentOption)) {
      setValue("viewStationsOption", defaultViewStationsDisplayOption);
    }
  }, [
    defaultViewStationsDisplayOption,
    getValues,
    rideDto.id,
    setValue,
    stationsDisplayOptions,
  ]);

  useEffect(() => {
    if (passengersDropdownList?.length) {
      return;
    }

    startTransition(() => {
      fetchDropdownPassengersAndStations();
    });
  }, [fetchDropdownPassengersAndStations, passengersDropdownList?.length]);

  useEffect(() => {
    if (!passengerWatchedId) {
      return;
    }

    const passengerDto = passengersDropdownList?.find(
      x => x.id === passengerWatchedId,
    );

    if (!passengerDto) {
      return;
    }

    const sourceStation1Condition =
      passengerDto?.sourceStation1Id &&
      (!onlyExistingStations ||
        existingOrPotentialStationIdsSet.has(passengerDto?.sourceStation1Id));

    const sourceStation2Condition =
      passengerDto?.sourceStation2Id &&
      (!onlyExistingStations ||
        existingOrPotentialStationIdsSet.has(passengerDto?.sourceStation2Id));

    const sourceStation3Condition =
      passengerDto?.sourceStation3Id &&
      (!onlyExistingStations ||
        existingOrPotentialStationIdsSet.has(passengerDto?.sourceStation3Id));

    const sourceStationList = isPickUp
      ? stationsFromDropdownList
      : stationsToDropdownList;

    if (
      sourceStation1Condition ||
      sourceStation2Condition ||
      sourceStation3Condition
    ) {
      if (isPickUp) {
        const pickUpStationOption = rideStationOptions.pickUp;
        const stationFromId = getPassengerSourceStationsIds({
          passengerDto,
          stationOption: pickUpStationOption,
        });

        const isInSourceList = sourceStationList.some(
          station => station.id === stationFromId,
        );

        setValue(
          "stationFromId",
          isInSourceList ? Number(stationFromId) : undefined,
        );
      } else {
        const dropOffStationOption = rideStationOptions.dropOff;
        const stationFromId = getPassengerSourceStationsIds({
          passengerDto,
          stationOption: dropOffStationOption,
        });

        const isInSourceList = sourceStationList.some(
          station => station.id === stationFromId,
        );

        setValue(
          "stationToId",
          isInSourceList ? Number(stationFromId) : undefined,
        );
      }
    } else {
      if (isPickUp) {
        setValue("stationFromId", undefined);
      } else {
        setValue("stationToId", undefined);
      }
    }

    // Destination uses this ride's direction option; Destination 1 still wins inside the helper (RYDE-8181).
    const destinationStationId = getPassengerDestinationStationIds({
      passengerDto,
      stationOption: isPickUp
        ? rideStationOptions.pickUp
        : rideStationOptions.dropOff,
    });

    const destinationStationAllowed =
      destinationStationId &&
      (!onlyExistingStations ||
        existingOrPotentialStationIdsSet.has(destinationStationId));

    const destinationStationList = isPickUp
      ? stationsToDropdownList
      : stationsFromDropdownList;

    if (destinationStationAllowed) {
      const isInDestinationList = destinationStationList.some(
        station => station.id === destinationStationId,
      );
      const selectedDestinationId = isInDestinationList
        ? Number(destinationStationId)
        : undefined;

      if (isPickUp) {
        setValue("stationToId", selectedDestinationId);
      } else {
        setValue("stationFromId", selectedDestinationId);
      }
    } else {
      if (isPickUp) {
        setValue("stationToId", undefined);
      } else {
        setValue("stationFromId", undefined);
      }
    }
  }, [
    direction,
    existingOrPotentialStationIdsSet,
    isPickUp,
    onlyExistingStations,
    passengerWatchedId,
    passengersDropdownList,
    rideStationOptions,
    setValue,
    stationsFromDropdownList,
    stationsToDropdownList,
  ]);

  const isLimitedPermissionsPassenger = Boolean(
    selectedPassenger?.isLimitedPermissionsSite,
  );

  return {
    methods,
    stationsDisplayOptions,
    openPassengerCard,
    createNewPassenger,
    stationsFromDropdownList,
    stationsToDropdownList,
    passengerToStationAddHandler,
    passengerDropdownListFiltered,
    openStationFromCard,
    openStationToCard,
    createNewStationTo,
    createNewStationFrom,
    boldStationsFromIds,
    boldStationsToIds,
    optionIconMapping,
    stationFromWatchedId,
    stationToWatchedId,
    passengerWatchedId,
    isPassengersDataLoading,
    isLimitedPermissionsPassenger,
  };
};
